package dev.danko.roblox_craft_ai.uikit.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.MutableState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.UserSelect
import com.varabyte.kobweb.compose.css.WhiteSpace
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import dev.danko.roblox_craft_ai.uikit.i18n.strings
import dev.danko.roblox_craft_ai.uikit.theme.LocalAppTheme
import dev.danko.roblox_craft_ai.uikit.util.Keyframe
import dev.danko.roblox_craft_ai.uikit.util.KeyframeSequence
import kotlinx.browser.document
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import kotlin.math.abs
import kotlin.math.ceil
import kotlin.math.floor
import kotlin.math.max

/** Body parts shown as timeline rows, with the R15 joints that belong to each. */
private val TRACKS: List<Pair<(dev.danko.roblox_craft_ai.uikit.i18n.AnimationSetupStrings) -> String, List<String>>> = listOf(
    { a: dev.danko.roblox_craft_ai.uikit.i18n.AnimationSetupStrings -> a.head } to listOf("Head"),
    { a: dev.danko.roblox_craft_ai.uikit.i18n.AnimationSetupStrings -> a.torso } to listOf("UpperTorso", "LowerTorso"),
    { a: dev.danko.roblox_craft_ai.uikit.i18n.AnimationSetupStrings -> a.leftArm } to listOf("LeftUpperArm", "LeftLowerArm", "LeftHand"),
    { a: dev.danko.roblox_craft_ai.uikit.i18n.AnimationSetupStrings -> a.rightArm } to listOf("RightUpperArm", "RightLowerArm", "RightHand"),
    { a: dev.danko.roblox_craft_ai.uikit.i18n.AnimationSetupStrings -> a.leftLeg } to listOf("LeftUpperLeg", "LeftLowerLeg", "LeftFoot"),
    { a: dev.danko.roblox_craft_ai.uikit.i18n.AnimationSetupStrings -> a.rightLeg } to listOf("RightUpperLeg", "RightLowerLeg", "RightFoot")
)

private const val ROW_PX = 22
private const val LABEL_PX = 104
/** A part "moves" at a key when one of its joints turned more than this (radians, ~3°) since the previous key. */
private const val MOVE_EPSILON = 0.05

/**
 * Dope-sheet timeline: a row per body part with a diamond where that part changes pose, a time ruler, a playhead
 * that can be clicked or dragged, keyframe stepping, speed, loop, and zoom of the time axis. Wraps instead of
 * cutting off controls in a narrow panel.
 */
@Composable
fun AnimationTimeline(
    duration: Double,
    keyframes: List<Keyframe>,
    isPlayingState: MutableState<Boolean>,
    currentTimeState: MutableState<Double>,
    speedState: MutableState<Double>,
    loopState: MutableState<Boolean>,
    onExportRbxm: () -> Unit
) {
    val palette = LocalAppTheme.current.colors
    val s = strings()
    val a = s.animationSetup
    var isPlaying by isPlayingState
    var currentTime by currentTimeState
    var speed by speedState
    var loop by loopState
    var timeZoom by remember { mutableStateOf(1) }
    var scrubbing by remember { mutableStateOf(false) }
    val trackId = remember { "anim-track-${kotlin.random.Random.nextInt(1_000_000)}" }

    val dur = if (duration > 0.0) duration else 1.0
    val keyTimes = keyframes.map { it.time.toDouble() }.distinct().sorted()

    fun seekTo(clientX: Double) {
        val rect = document.getElementById(trackId)?.asDynamic()?.getBoundingClientRect() ?: return
        val width = (rect.width as Number).toDouble().takeIf { it > 0 } ?: return
        val ratio = ((clientX - (rect.left as Number).toDouble()) / width).coerceIn(0.0, 1.0)
        currentTime = ratio * dur
    }

    fun step(forward: Boolean) {
        isPlaying = false
        currentTime = if (forward) keyTimes.firstOrNull { it > currentTime + 0.001 } ?: keyTimes.lastOrNull() ?: dur
        else keyTimes.lastOrNull { it < currentTime - 0.001 } ?: 0.0
    }

    Column(TimelineSurfaceStyle.toModifier().gap(8.px)) {
        Row(Modifier.fillMaxWidth().gap(6.px).flexWrap(FlexWrap.Wrap), verticalAlignment = Alignment.CenterVertically) {
            TimelineButton("⏮", a.prevKey) { step(forward = false) }
            AppButton(
                text = if (isPlaying) s.meshViewer.pause else s.meshViewer.play,
                iconLeft = if (isPlaying) "⏸" else "▶",
                variant = if (isPlaying) AppButtonVariant.PRIMARY else AppButtonVariant.OUTLINE,
                size = AppButtonSize.SM,
                onClick = {
                    if (!isPlaying && currentTime >= dur - 0.001) currentTime = 0.0
                    isPlaying = !isPlaying
                }
            )
            TimelineButton("⏭", a.nextKey) { step(forward = true) }
            TimelineButton("⏹", s.meshViewer.stop) {
                isPlaying = false
                currentTime = 0.0
            }
            SpanText(
                "${formatSeconds(currentTime)} / ${formatSeconds(dur)} s",
                Modifier.fontSize(13.px).fontFamily("ui-monospace", "SFMono-Regular", "Menlo", "monospace")
                    .color(palette.textPrimary).whiteSpace(WhiteSpace.NoWrap).padding(leftRight = 6.px)
            )
            listOf(0.5, 1.0, 2.0).forEach { value ->
                AppButton(
                    text = "${value}x",
                    variant = if (speed == value) AppButtonVariant.PRIMARY else AppButtonVariant.OUTLINE,
                    size = AppButtonSize.SM,
                    onClick = { speed = value }
                )
            }
            AppButton(
                text = if (loop) s.meshViewer.loopOn else s.meshViewer.loopOff,
                variant = if (loop) AppButtonVariant.PRIMARY else AppButtonVariant.OUTLINE,
                size = AppButtonSize.SM,
                onClick = { loop = !loop }
            )
            Spacer()
            TimelineButton("−", a.timeZoom) { timeZoom = max(1, timeZoom / 2) }
            SpanText("${timeZoom}×", Modifier.fontSize(12.px).color(palette.textSecondary).minWidth(22.px))
            TimelineButton("+", a.timeZoom) { timeZoom = (timeZoom * 2).coerceAtMost(8) }
            AppButton(text = s.meshViewer.exportKeyframes, iconLeft = "📥", variant = AppButtonVariant.OUTLINE, size = AppButtonSize.SM, onClick = onExportRbxm)
        }

        Row(Modifier.fillMaxWidth()) {
            Column(Modifier.width(LABEL_PX.px).flexShrink(0)) {
                Box(Modifier.height(ROW_PX.px))
                TRACKS.forEach { (label, _) ->
                    SpanText(
                        label(a),
                        Modifier.height(ROW_PX.px).fontSize(12.px).color(palette.textSecondary).whiteSpace(WhiteSpace.NoWrap)
                            .overflow(Overflow.Hidden).styleModifier { property("line-height", "${ROW_PX}px") }
                    )
                }
            }
            // Scrolls sideways when the time axis is zoomed in.
            Div(Modifier.flexGrow(1).overflow { x(Overflow.Auto); y(Overflow.Hidden) }.toAttrs()) {
                Div(
                    Modifier
                        .id(trackId)
                        // Room for the half of a diamond that sits past the last key, so nothing is cut off.
                        .styleModifier { property("width", "calc(${timeZoom * 100}% - 12px)") }
                        .margin(leftRight = 6.px)
                        .position(Position.Relative)
                        .cursor(Cursor.Pointer)
                        .userSelect(UserSelect.None)
                        .onMouseDown { event ->
                            isPlaying = false
                            scrubbing = true
                            seekTo(event.nativeEvent.asDynamic().clientX as Double)
                        }
                        .onMouseMove { event -> if (scrubbing) seekTo(event.nativeEvent.asDynamic().clientX as Double) }
                        .onMouseUp { scrubbing = false }
                        .onMouseLeave { scrubbing = false }
                        .toAttrs()
                ) {
                    Ruler(dur, timeZoom)
                    TRACKS.forEachIndexed { row, (_, joints) ->
                        Box(
                            Modifier.fillMaxWidth().height(ROW_PX.px).position(Position.Relative)
                                .backgroundColor(if (row % 2 == 0) palette.surface else palette.background)
                        ) {
                            keyframes.forEachIndexed { index, keyframe ->
                                if (index == 0 || moves(keyframes[index - 1], keyframe, joints)) {
                                    val left = (keyframe.time / dur * 100).coerceIn(0.0, 100.0)
                                    Box(
                                        Modifier.position(Position.Absolute).left(left.percent).top(6.px).size(10.px)
                                            .margin(left = (-5).px).backgroundColor(palette.accent)
                                            .border(1.px, LineStyle.Solid, palette.surfaceElevated)
                                            .styleModifier { property("transform", "rotate(45deg)") }
                                    )
                                }
                            }
                        }
                    }
                    // Playhead across the ruler and all rows.
                    Box(
                        Modifier.position(Position.Absolute).top(0.px).bottom(0.px)
                            .left((currentTime / dur * 100).coerceIn(0.0, 100.0).percent)
                            .width(2.px).margin(left = (-1).px).backgroundColor(palette.statusPending)
                    )
                }
            }
        }
    }
}

@Composable
private fun Ruler(duration: Double, zoom: Int) {
    val palette = LocalAppTheme.current.colors
    // About ten labelled ticks across the visible width, on round values.
    val target = duration / (10.0 * zoom)
    val stepSeconds = listOf(0.05, 0.1, 0.2, 0.25, 0.5, 1.0, 2.0).firstOrNull { it >= target } ?: 2.0
    val count = ceil(duration / stepSeconds + 1e-6).toInt()
    Box(Modifier.fillMaxWidth().height(ROW_PX.px).position(Position.Relative).styleModifier { property("border-bottom", "1px solid ${palette.border}") }) {
        for (i in 0..count) {
            val t = (i * stepSeconds).coerceAtMost(duration)
            val left = t / duration * 100
            Box(Modifier.position(Position.Absolute).left(left.percent).bottom(0.px).width(1.px).height(6.px).backgroundColor(palette.borderStrong))
            if (i < count || abs(t - duration) < 1e-6) {
                val last = i == count || abs(t - duration) < 1e-6
                SpanText(
                    "${formatSeconds(t)}s",
                    // The label of the end mark sits left of it, inside the track.
                    Modifier.position(Position.Absolute).left(left.percent).top(2.px).margin(left = 3.px)
                        .styleModifier { if (last) property("transform", "translateX(calc(-100% - 6px))") }
                        .fontSize(10.px).color(palette.textSecondary).whiteSpace(WhiteSpace.NoWrap)
                )
            }
        }
    }
}

@Composable
private fun TimelineButton(symbol: String, label: String, onClick: () -> Unit) {
    AppButton(
        text = symbol,
        variant = AppButtonVariant.OUTLINE,
        size = AppButtonSize.SM,
        ariaLabel = label,
        modifier = Modifier.minWidth(34.px).title(label).fontWeight(FontWeight.Bold),
        onClick = onClick
    )
}

/** Whether one of [joints] changes between two keys enough to show a diamond. */
private fun moves(before: Keyframe, after: Keyframe, joints: List<String>): Boolean = joints.any { joint ->
    val a = before.poses[joint]
    val b = after.poses[joint]
    if (a == null || b == null) return@any a != b
    abs(a.rotation.first - b.rotation.first) > MOVE_EPSILON || abs(a.rotation.second - b.rotation.second) > MOVE_EPSILON ||
        abs(a.rotation.third - b.rotation.third) > MOVE_EPSILON || abs(a.position.first - b.position.first) > 0.05 ||
        abs(a.position.second - b.position.second) > 0.05 || abs(a.position.third - b.position.third) > 0.05
}

/** Clip facts and every joint's angles at the playhead, for the animation workspace. */
@Composable
fun AnimationSidePanel(sequence: KeyframeSequence?, time: Double, modifier: Modifier = Modifier) {
    val palette = LocalAppTheme.current.colors
    val a = strings().animationSetup
    Column(
        modifier.width(290.px).padding(12.px).gap(6.px).borderRadius(12.px)
            .backgroundColor(palette.surface.toRgb().copy(alpha = 235)).border(1.px, LineStyle.Solid, palette.borderStrong)
            .styleModifier { property("max-height", "calc(100vh - 380px)"); property("overflow-y", "auto") }
    ) {
        SpanText(a.clipTitle, Modifier.fontSize(12.px).fontWeight(FontWeight.SemiBold).color(palette.textSecondary))
        if (sequence == null) return@Column
        SpanText(sequence.name ?: "—", Modifier.fontSize(14.px).fontWeight(FontWeight.Bold).color(palette.textPrimary))
        SpanText(
            "${a.duration}: ${formatSeconds(sequence.duration.toDouble())} s · ${if (sequence.looped) a.looped else a.once} · ${a.keys}: ${sequence.keyframes.size}",
            Modifier.fontSize(12.px).color(palette.textSecondary)
        )
        SpanText(a.jointsTitle, Modifier.fontSize(12.px).fontWeight(FontWeight.SemiBold).color(palette.textSecondary).margin(top = 6.px))
        TRACKS.flatMap { it.second }.forEach { joint ->
            val angles = anglesAt(sequence.keyframes, joint, time) ?: return@forEach
            Row(Modifier.fillMaxWidth().gap(6.px), verticalAlignment = Alignment.CenterVertically) {
                SpanText(joint, Modifier.fontSize(11.px).color(palette.textSecondary).width(104.px).flexShrink(0).whiteSpace(WhiteSpace.NoWrap))
                SpanText(
                    angles.joinToString(" ") { "${it.toInt()}°".padStart(5) },
                    Modifier.fontSize(11.px).fontFamily("ui-monospace", "SFMono-Regular", "Menlo", "monospace")
                        .color(palette.textPrimary).flexShrink(0).styleModifier { property("white-space", "pre") }
                )
            }
        }
    }
}

/** A joint's x/y/z degrees at [time], straight between the surrounding keys; null when never keyed. */
private fun anglesAt(keyframes: List<Keyframe>, joint: String, time: Double): List<Double>? {
    val keyed = keyframes.filter { joint in it.poses }
    if (keyed.isEmpty()) return null
    val next = keyed.firstOrNull { it.time >= time } ?: keyed.last()
    val prev = keyed.lastOrNull { it.time <= time } ?: keyed.first()
    val a = prev.poses.getValue(joint).rotation
    val b = next.poses.getValue(joint).rotation
    val span = (next.time - prev.time).toDouble()
    val k = if (span > 1e-6) ((time - prev.time) / span).coerceIn(0.0, 1.0) else 0.0
    return listOf(a.first + (b.first - a.first) * k, a.second + (b.second - a.second) * k, a.third + (b.third - a.third) * k)
        .map { it * 180 / kotlin.math.PI }
}

internal fun formatSeconds(value: Double): String {
    val hundredths = floor(value * 100 + 1e-6).toLong()
    return "${hundredths / 100}.${(hundredths % 100).toString().padStart(2, '0')}"
}
